import React from 'react';
import { Smartphone, Battery, Cable, Droplets, SearchCheck, Clock, CheckCircle, Laptop, MonitorSmartphone, Gamepad2 } from 'lucide-react';
import { services } from '../mockData';

const Services = () => {
  const serviceImage = "https://images.unsplash.com/photo-1531297484001-80022131f5a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHx0ZWNobm9sb2d5fGVufDB8fHx8MTc2ODQxNTYwOXww&ixlib=rb-4.1.0&q=85";
  const repairImage = "https://images.unsplash.com/photo-1568952433726-3896e3881c65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHx0ZWNobm9sb2d5fGVufDB8fHx8MTc2ODQxNTYwOXww&ixlib=rb-4.1.0&q=85";

  const getIcon = (iconName) => {
    const icons = {
      Smartphone, Battery, Cable, Droplets, SearchCheck, Clock, Laptop, MonitorSmartphone, Gamepad2
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="w-6 h-6" /> : <Smartphone className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Header Section */}
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Our Repair Services
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Professional repair services for phones, laptops, computers, and game consoles. Most repairs completed same-day with quality parts and expert care.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mb-16">
            <div>
              <img 
                src={serviceImage}
                alt="Phone repair technician at work"
                className="rounded-2xl shadow-2xl border border-neutral-800"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Expert Device Repairs You Can Trust
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Certified Technicians</p>
                    <p className="text-gray-400 text-sm">Years of experience with all major brands</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Quality Parts</p>
                    <p className="text-gray-400 text-sm">High-quality replacements backed by warranty</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Fast Turnaround</p>
                    <p className="text-gray-400 text-sm">Most repairs done in 30-60 minutes</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Honest Pricing</p>
                    <p className="text-gray-400 text-sm">No hidden fees, upfront quotes</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div 
                key={service.id}
                className="group p-6 bg-neutral-900 border border-neutral-800 rounded-xl hover:border-white/50 transition-all hover:shadow-lg hover:shadow-white/10"
              >
                <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center text-white mb-4 group-hover:bg-white/20 transition-colors">
                  {getIcon(service.icon)}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-white transition-colors">
                  {service.name}
                </h3>
                <p className="text-gray-400 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <div className="flex flex-col gap-2 pt-4 border-t border-neutral-800">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Price:</span>
                    <span className="text-white font-semibold">{service.price}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Time:</span>
                    <span className="text-gray-300 font-medium text-sm">{service.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-neutral-900/50 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Our Repair Process
            </h2>
            <p className="text-gray-400 text-lg">
              Simple, transparent, and fast
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-white mx-auto mb-4 border-2 border-white/30">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Bring It In</h3>
              <p className="text-gray-400">Walk in or call ahead. We accept all phones.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-white mx-auto mb-4 border-2 border-white/30">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Free Diagnosis</h3>
              <p className="text-gray-400">We inspect your device and provide an honest quote.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-white mx-auto mb-4 border-2 border-white/30">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Expert Repair</h3>
              <p className="text-gray-400">Our technicians fix your phone with quality parts.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-white mx-auto mb-4 border-2 border-white/30">
                <span className="text-2xl font-bold">4</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Quality Check</h3>
              <p className="text-gray-400">We test everything before returning your device.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Device Brands Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              We Repair All Major Brands & Devices
            </h2>
            <p className="text-gray-400 text-lg">
              Phones, Laptops, Computers, and Game Consoles
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Apple iPhone', 'Samsung Galaxy', 'Google Pixel', 'LG & Motorola', 
              'MacBook & Laptops', 'Dell & HP', 'PlayStation', 'Xbox & Switch'].map((brand, index) => (
              <div 
                key={index}
                className="p-6 bg-neutral-900 border border-neutral-800 rounded-xl text-center hover:border-white/50 transition-all"
              >
                <p className="text-white font-semibold">{brand}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 lg:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
            <div className="relative">
              <img 
                src={repairImage}
                alt="Cracked phone screen"
                className="w-32 h-32 mx-auto rounded-2xl mb-6 border border-neutral-700 object-cover"
              />
              <h2 className="text-3xl font-bold text-white mb-4">
                Need a Device Repaired? We're Here to Help
              </h2>
              <p className="text-gray-300 mb-8 text-lg">
                Call us now or stop by. Walk-ins welcome, most repairs done same-day!
              </p>
              <a
                href="tel:(915) 787-9448"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-black font-bold text-white font-semibold rounded-xl transition-all shadow-lg shadow-white/30 hover:shadow-white/50 hover:scale-105"
              >
                Call Now: (915) 787-9448
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
